import React, { Suspense } from 'react';
import Fullscreen from 'react-full-screen';
import { NavBar } from './NavBar';
import { Breadcrumb } from './Breadcrumb';

import { Navigation } from './Navigation';
import Loader from '../Loader';
import { useAppMenu } from 'App/menu-items';
import { AppFooter } from './AppFooter';
import { useAppRootActions, useAppRootState } from 'App/root/context';
import { useWindowSize } from 'App/ui-utils';
import { appEnv } from 'App/utils';
import { useAppUser } from 'App/components/utils/providers/AppUserProvider';
import { Trans, useTranslation } from 'react-i18next';
import { AppPageContext, AppPageContextType } from 'App/components/utils/pages/context';
import { AppLayoutAction, appLayoutReducer,
  AppLayoutReducer, initialAppLayoutState } from './reducers/app-layout-reducer';
import { resolveEnvironmentBanner } from './environment-banner';

export const AppLayout: React.FC = ( props ) => {
  const { t } = useTranslation( [ 'base' ] );
  const currentAccount = useAppUser();
  const windowSize = useWindowSize();
  const menuItems = useAppMenu();
  const { menu: menuActions } = useAppRootActions();
  const { menu, screen } = useAppRootState();
  const [ appLayoutState, dispatch ] = React.useReducer<AppLayoutReducer>( appLayoutReducer, initialAppLayoutState );
  const { workspaceName } = appLayoutState;

  const ctx = React.useMemo<AppPageContextType<AppLayoutAction>>( () => {
    return {
      fireAction: dispatch,
    };
  }, [] );

  const mobileOutClickHandler = React.useCallback( () => {
    if ( windowSize.width < 992 && menu.collapseMenu ) {
      menuActions.collapseMenu();
    }
  }, [ menu.collapseMenu, menuActions, windowSize.width ] );

  const banner = resolveEnvironmentBanner( appEnv, currentAccount.tenantSlug );
  const bannerWorkspace = workspaceName ? ` (${workspaceName})` : '';

  return (
    <AppPageContext.Provider value={ ctx }>
      <div className={ banner.visible ? `app-mode ${banner.modeClass ?? ''}`.trim() : '' }>
        { banner.visible && (
          <div
            className="notification-env-mode"
            style={ banner.generatedStyle
              ? { backgroundColor: banner.generatedStyle.backgroundColor }
              : undefined }
          >
            <p style={ banner.generatedStyle ? { color: banner.generatedStyle.textColor } : undefined }>
              <Trans
                t={ t }
                i18nKey={ banner.presentedAs ? 'environmentModePresented' : 'environmentMode' }
                values={ { environment: banner.environmentName,
                  presentedAs: banner.presentedAs, workspace: bannerWorkspace } }
                components={ { b: <b /> } }
              />
            </p>
          </div>
        ) }
        <Fullscreen enabled={ screen.isFullScreen }>
          <Navigation menuItems={ menuItems } />
          <NavBar />
          <div className="pcoded-main-container" onClick={ mobileOutClickHandler }>
            <div className="pcoded-wrapper app-vertical-container">
              <div className="pcoded-content pt-0">
                <div className="pcoded-inner-content">
                  <Breadcrumb menuItems={ menuItems } />
                  <div className="main-body">
                    <div className="page-wrapper">
                      <Suspense fallback={ <Loader /> }>
                        { props.children }
                      </Suspense>
                    </div>
                  </div>
                </div>
              </div>
              <AppFooter />
            </div>
          </div>
        </Fullscreen>
      </div>
    </AppPageContext.Provider>
  );
};
